/**
 * /frontend/src/features/analytics/pages/WhatIfAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useWhatIfAnalytics from '../hooks/useWhatIfAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import ScenarioSliders from '../components/whatif/ScenarioSliders';
import CurrentVsProjectedChart from '../components/whatif/CurrentVsProjectedChart';
import { asNumber } from '../utils/formatters';
import './WhatIfAnalyticsPage.css';

const toComparisonRows = (currentMetrics, projectedMetrics) => ([
  { metric: 'Avg Wait (min)', current: asNumber(currentMetrics?.avgWaitMinutes), projected: asNumber(projectedMetrics?.avgWaitMinutes) },
  { metric: 'Throughput/hr', current: asNumber(currentMetrics?.throughputPerHour), projected: asNumber(projectedMetrics?.throughputPerHour) },
  { metric: 'Abandonment %', current: asNumber(currentMetrics?.abandonmentRate), projected: asNumber(projectedMetrics?.abandonmentRate) }
]);

const computeSectionsScenario = (base, addSections, removeSections) => {
  const current = base?.currentMetrics || {};
  const currentSections = Math.max(1, asNumber(base?.currentSections, 1));
  const projectedSections = Math.max(1, currentSections + asNumber(addSections) - asNumber(removeSections));
  const ratio = currentSections / projectedSections;
  return {
    currentMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes),
      throughputPerHour: asNumber(current?.throughputPerHour),
      abandonmentRate: asNumber(current?.abandonmentRate)
    },
    projectedMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes) * ratio,
      throughputPerHour: asNumber(current?.throughputPerHour) * (1 / ratio),
      abandonmentRate: asNumber(current?.abandonmentRate) * ratio
    }
  };
};

const computeStaffScenario = (base, additionalStaff, reducedStaff) => {
  const current = base?.currentMetrics || {};
  const currentStaff = Math.max(1, asNumber(base?.currentStaff, 1));
  const projectedStaff = Math.max(1, currentStaff + asNumber(additionalStaff) - asNumber(reducedStaff));
  const ratio = projectedStaff / currentStaff;
  return {
    currentMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes),
      throughputPerHour: asNumber(current?.throughputPerHour),
      abandonmentRate: asNumber(current?.abandonmentRate)
    },
    projectedMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes) / ratio,
      throughputPerHour: asNumber(current?.throughputPerHour) * ratio,
      abandonmentRate: asNumber(current?.abandonmentRate) / ratio
    }
  };
};

const computeHoursScenario = (base, shiftStartBy, shiftEndBy) => {
  const current = base?.currentMetrics || {};
  const startShift = asNumber(shiftStartBy);
  const endShift = asNumber(shiftEndBy);
  const coverageRatio = Math.max(0.3, 1 + ((endShift - startShift) * 0.06) - ((Math.abs(startShift) + Math.abs(endShift)) * 0.03));
  return {
    currentMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes),
      throughputPerHour: asNumber(current?.throughputPerHour),
      abandonmentRate: asNumber(current?.abandonmentRate)
    },
    projectedMetrics: {
      avgWaitMinutes: asNumber(current?.avgWaitMinutes) * (1 / coverageRatio),
      throughputPerHour: asNumber(current?.throughputPerHour) * coverageRatio,
      abandonmentRate: asNumber(current?.abandonmentRate) * (1 / coverageRatio)
    }
  };
};

/**
 * What-if analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const WhatIfAnalyticsPage = ({ request, storeToken, storeUuid, refreshSignal, onSyncingChange }) => {
  const hook = useWhatIfAnalytics({ request, storeToken, storeUuid, refreshSignal });
  const [sectionsScenario, setSectionsScenario] = useState({ addSections: 0, removeSections: 0 });
  const [staffScenario, setStaffScenario] = useState({ additionalStaff: 0, reducedStaff: 0 });
  const [hoursScenario, setHoursScenario] = useState({ shiftStartBy: 0, shiftEndBy: 0 });
  const syncing = hook.isLoading || hook.isRefreshing;

  const sectionsResult = useMemo(
    () => computeSectionsScenario(hook.data?.sections, sectionsScenario.addSections, sectionsScenario.removeSections),
    [hook.data?.sections, sectionsScenario.addSections, sectionsScenario.removeSections]
  );
  const staffResult = useMemo(
    () => computeStaffScenario(hook.data?.staff, staffScenario.additionalStaff, staffScenario.reducedStaff),
    [hook.data?.staff, staffScenario.additionalStaff, staffScenario.reducedStaff]
  );
  const hoursResult = useMemo(
    () => computeHoursScenario(hook.data?.hours, hoursScenario.shiftStartBy, hoursScenario.shiftEndBy),
    [hook.data?.hours, hoursScenario.shiftEndBy, hoursScenario.shiftStartBy]
  );

  const sectionsRows = useMemo(() => toComparisonRows(sectionsResult.currentMetrics, sectionsResult.projectedMetrics), [sectionsResult]);
  const staffRows = useMemo(() => toComparisonRows(staffResult.currentMetrics, staffResult.projectedMetrics), [staffResult]);
  const hoursRows = useMemo(() => toComparisonRows(hoursResult.currentMetrics, hoursResult.projectedMetrics), [hoursResult]);

  const isEmpty = useMemo(() => !hook.data?.sections && !hook.data?.staff && !hook.data?.hours, [hook.data?.hours, hook.data?.sections, hook.data?.staff]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No what-if analytics data available yet." />;

  return (
    <div className="analytics-page-stack">
      <ScenarioSliders
        sections={sectionsScenario}
        staff={staffScenario}
        hours={hoursScenario}
        onSectionsChange={setSectionsScenario}
        onStaffChange={setStaffScenario}
        onHoursChange={setHoursScenario}
      />
      <div className="analytics-page-grid-3">
        <CurrentVsProjectedChart title="Section Scenario" data={sectionsRows} />
        <CurrentVsProjectedChart title="Staff Scenario" data={staffRows} />
        <CurrentVsProjectedChart title="Hours Scenario" data={hoursRows} />
      </div>
    </div>
  );
};

export default WhatIfAnalyticsPage;
